// The phone's error reports (OP-06). The Sentry browser SDK, loaded after the first screen and only
// when the api names a project, sends through our own /api/v1/errors (its tunnel), so nothing in
// the Lagos Life frame talks to another host and GlitchTip never sees a player's address. No user,
// no IP, no query strings: a join code or a ticket must never ride along.
import type { ApiConfig } from './api';

/** The deploy's release name, baked in at build time (vite.config.ts). */
declare const __RELEASE__: string;

type Scrubbable = { request?: { url?: string; headers?: Record<string, string> }; user?: unknown };

/** What leaves the phone: the path without its query or hash, no headers but the browser's. */
export function scrubEvent<E extends Scrubbable>(event: E): E {
  if (event.request?.url) event.request.url = event.request.url.split(/[?#]/)[0] ?? '';
  if (event.request?.headers) {
    const ua = event.request.headers['User-Agent'];
    event.request.headers = ua ? { 'User-Agent': ua } : {};
  }
  delete event.user;
  return event;
}

let started = false;

export async function startErrors(errors: NonNullable<ApiConfig['errors']>): Promise<void> {
  if (started) return;
  started = true;
  const Sentry = await import('./errors-sdk');
  Sentry.init({
    dsn: errors.dsn,
    tunnel: '/api/v1/errors',
    environment: errors.environment,
    release: typeof __RELEASE__ === 'string' && __RELEASE__ ? __RELEASE__ : undefined,
    sampleRate: errors.sampleRate,
    sendDefaultPii: false,
    // errors only: no tracing, no session replay, no session pings
    integrations: (all) => all.filter((i) => i.name !== 'BrowserSession'),
    beforeSend: (event) => scrubEvent(event),
    beforeBreadcrumb: (crumb) => {
      // a fetch or a page address can carry a code in its query
      if (crumb.data && typeof crumb.data.url === 'string') crumb.data.url = crumb.data.url.split(/[?#]/)[0];
      if (crumb.data && typeof crumb.data.to === 'string') crumb.data.to = crumb.data.to.split(/[?#]/)[0];
      if (crumb.data && typeof crumb.data.from === 'string') crumb.data.from = crumb.data.from.split(/[?#]/)[0];
      return crumb;
    },
  });
}
